import { Box } from '@mui/material';
import React, { useState } from 'react';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import Tooltip from '@mui/material/Tooltip';

interface CopyToClipboardProps {
  textToCopy: string;
  formattedContent?: HTMLElement | null;
  preserveFormatting?: boolean;
}

const CopyToClipboard: React.FC<CopyToClipboardProps> = ({ textToCopy, formattedContent, preserveFormatting }) => {
  const [showCopiedIcon, setShowCopiedIcon] = useState(false);
  const COPY_DELAY = 2000;

  const copyResponseText = async () => {
    try {
      if (preserveFormatting && formattedContent) {
        try {
          // Copy with HTML formatting preserved like bold text, bullet points, etc.
          const renderedHTML = formattedContent.innerHTML;
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([renderedHTML], { type: 'text/html' }),
              'text/plain': new Blob([formattedContent.innerText], { type: 'text/plain' }),
            }),
          ]);
        } catch (formattedError) {
          // If HTML copying fails, fall back to plain text
          await navigator.clipboard.writeText(textToCopy);
        }
      } else {
        // Standard copy for code blocks or plain text
        await navigator.clipboard.writeText(textToCopy);
      }
      setShowCopiedIcon(true);
    } catch (error) {
      setShowCopiedIcon(false);
    } finally {
      setTimeout(() => {
        setShowCopiedIcon(false);
      }, COPY_DELAY);
    }
  };

  return (
    <>
      <Tooltip title={showCopiedIcon ? 'Copied' : 'Copy'} arrow>
        <Box
          role="button"
          tabIndex={0}
          aria-label="Copy to clipboard"
          sx={{ cursor: showCopiedIcon ? 'default' : 'pointer' }}
          onClick={copyResponseText}
        >
          {showCopiedIcon ? <DoneRoundedIcon /> : <ContentCopyRoundedIcon />}
        </Box>
      </Tooltip>
    </>
  );
};
export default CopyToClipboard;
